// Morrow — Connect a built-in app (DIA, Entegra).
//
// Business systems Morrow wraps itself (no connector platform ships them). They
// connect like any Composio app: the card's Connect button opens a sign-in
// dialog on the Connections page (AppConnectDialog); /settings/app-connect/<app>
// shows the same form as a page for deep links. The form is built from the
// app's own field list (GET /api/morrow/apps); the server signs in to the real
// app to check the login BEFORE storing it encrypted, and never sends it back.
//
// Backed by /api/morrow/apps (+ /connections, /connect, /disconnect). Requests
// carry the Clerk token; the server acts only for the verified signed-in user.

import { ArrowLeftIcon, CheckIcon, Loader2Icon, ShieldCheckIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { apiUrl } from "../../apiBase";
import { getClerkToken } from "../../clerkTokenBridge";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { canSubmitCredentials } from "./appConnectForm.logic";
import { builtinAppLogo } from "./builtinApps";
import { CredentialFieldInputs } from "./CredentialFieldInputs";
import { SettingsPageContainer } from "./settingsLayout";

export type AppField = {
  key: string;
  label: string;
  type: "text" | "password" | "email";
  required: boolean;
  placeholder?: string;
  help?: string;
};
export type BuiltinAppInfo = {
  id: string;
  name: string;
  description: string;
  accessNote: string;
  fields: AppField[];
  toolCount: number;
};
export type AppConnection = {
  appId: string;
  accountLabel: string;
  status: string;
  lastError: string | null;
  connectedAt: string;
};

async function appRequest<T>(
  path: string,
  body?: Record<string, unknown>,
): Promise<{ ok: boolean; data: (T & { error?: string }) | null }> {
  try {
    const token = await getClerkToken().catch(() => null);
    const headers: Record<string, string> = { accept: "application/json" };
    if (token) headers["x-clerk-token"] = token;
    if (body) headers["content-type"] = "application/json";
    const res = await fetch(apiUrl(path), {
      method: body ? "POST" : "GET",
      headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
    return { ok: res.ok, data };
  } catch {
    return { ok: false, data: null };
  }
}

/** The built-in app catalog (public). */
export async function fetchBuiltinApps(): Promise<BuiltinAppInfo[]> {
  const { ok, data } = await appRequest<{ apps?: BuiltinAppInfo[] }>("/api/morrow/apps");
  return ok && Array.isArray(data?.apps) ? data.apps : [];
}

/** The signed-in user's built-in app connections ([] when signed out or on error). */
export async function fetchAppConnections(): Promise<AppConnection[]> {
  const { ok, data } = await appRequest<{ connections?: AppConnection[] }>(
    "/api/morrow/apps/connections",
  );
  return ok && Array.isArray(data?.connections) ? data.connections : [];
}

/** Disconnect a built-in app. Resolves true when it's gone. */
export async function disconnectBuiltinApp(appId: string): Promise<boolean> {
  const { ok } = await appRequest("/api/morrow/apps/disconnect", { appId });
  return ok;
}

/** The app's logo in the same white tile Composio toolkits use. */
export function BuiltinAppLogo({ app, size = "md" }: { app: BuiltinAppInfo; size?: "md" | "lg" }) {
  const [failed, setFailed] = useState(false);
  const src = builtinAppLogo(app.id);
  const box = size === "lg" ? "size-14" : "size-12";
  if (failed || !src) {
    return (
      <span
        className={`flex ${box} items-center justify-center rounded-lg border border-border bg-muted text-sm font-semibold uppercase text-muted-foreground`}
      >
        {app.name.slice(0, 2)}
      </span>
    );
  }
  return (
    <div
      className={`flex ${box} items-center justify-center overflow-hidden rounded-lg border border-border bg-white dark:bg-white`}
    >
      <img
        src={src}
        alt={`${app.name} logo`}
        onError={() => setFailed(true)}
        className="size-full object-cover"
      />
    </div>
  );
}

/** The sign-in fields + Connect button. Calls onConnected with the new connection. */
export function AppConnectForm({
  app,
  onConnected,
}: {
  app: BuiltinAppInfo;
  onConnected: (connection: AppConnection) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async () => {
    setBusy(true);
    setError(null);
    const { ok, data } = await appRequest<{ connection?: AppConnection }>(
      "/api/morrow/apps/connect",
      { appId: app.id, credentials: values },
    );
    setBusy(false);
    if (!ok || !data?.connection) {
      setError(data?.error ?? "Couldn't connect. Try again.");
      return;
    }
    setValues({});
    onConnected(data.connection);
  }, [app.id, onConnected, values]);

  const canConnect = canSubmitCredentials({ fields: app.fields, values, busy });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canConnect) void connect();
      }}
    >
      <CredentialFieldInputs
        fields={app.fields}
        values={values}
        onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))}
      />

      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <Button type="submit" disabled={!canConnect} className="w-full">
        {busy ? (
          <>
            <Loader2Icon className="size-4 animate-spin" /> Signing in to {app.name}…
          </>
        ) : (
          <>
            <ShieldCheckIcon className="size-4" /> Connect
          </>
        )}
      </Button>
      <p className="text-center text-[11px] text-muted-foreground">
        We sign in once to check it works, then store it encrypted. It's never shown again.{" "}
        {app.accessNote}
      </p>
    </form>
  );
}

/** Connect dialog opened from an app's card on the Connections page. */
export function AppConnectDialog({
  app,
  onOpenChange,
  onConnected,
}: {
  app: BuiltinAppInfo | null;
  onOpenChange: (open: boolean) => void;
  onConnected: (connection: AppConnection) => void;
}) {
  return (
    <Dialog open={app !== null} onOpenChange={onOpenChange}>
      {app ? (
        <DialogPopup className="max-w-md">
          <DialogHeader className="items-center text-center">
            <BuiltinAppLogo app={app} size="lg" />
            <DialogTitle>Connect {app.name}</DialogTitle>
            <DialogDescription>{app.description}</DialogDescription>
          </DialogHeader>
          <DialogPanel>
            <AppConnectForm app={app} onConnected={onConnected} />
          </DialogPanel>
        </DialogPopup>
      ) : null}
    </Dialog>
  );
}

/** /settings/app-connect/<app> — the same form as a page (deep links). */
export function AppConnectPage({ appId }: { appId: string }) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, openSignIn } = useMorrowClerkAuth();

  const [app, setApp] = useState<BuiltinAppInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<AppConnection | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void fetchBuiltinApps().then((apps) => {
      setApp(apps.find((a) => a.id === appId) ?? null);
      setLoading(false);
    });
  }, [appId]);

  useEffect(() => {
    if (!isSignedIn) return;
    void fetchAppConnections().then((list) =>
      setConnection(list.find((c) => c.appId === appId) ?? null),
    );
  }, [appId, isSignedIn]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    if (await disconnectBuiltinApp(appId)) setConnection(null);
    setBusy(false);
  }, [appId]);

  return (
    <SettingsPageContainer>
      <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6">
        <button
          type="button"
          onClick={() => void navigate({ to: "/settings/connections" })}
          className="flex items-center gap-1.5 self-start text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-3.5" /> Connections
        </button>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : !app ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            That app isn't available.
          </p>
        ) : (
          <>
            <div className="flex flex-col items-center gap-2 text-center">
              <BuiltinAppLogo app={app} size="lg" />
              <h1 className="text-lg font-semibold text-foreground">Connect {app.name}</h1>
              <p className="max-w-md text-sm text-muted-foreground">{app.description}</p>
            </div>

            {isLoaded && !isSignedIn ? (
              <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-5 text-center">
                <p className="text-sm text-muted-foreground">Sign in to connect {app.name}.</p>
                <Button onClick={() => openSignIn()}>Sign in</Button>
              </div>
            ) : connection ? (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card p-5">
                <div className="flex min-w-0 items-center gap-2">
                  <CheckIcon className="size-4 shrink-0 text-emerald-500" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      Connected as {connection.accountLabel}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Morrow can use {app.toolCount} {app.name} tools in chats, agents and
                      workflows.
                    </div>
                  </div>
                </div>
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label={`Disconnect ${app.name}`}
                  disabled={busy}
                  onClick={() => void disconnect()}
                >
                  <XIcon className="size-3.5" />
                </Button>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-card p-5">
                <AppConnectForm app={app} onConnected={setConnection} />
              </div>
            )}
          </>
        )}
      </div>
    </SettingsPageContainer>
  );
}
